import {Pipe, PipeTransform} from '@angular/core';
import {ReportItemTemplateField} from '@domain/report-data-set/report-item-template-field.enum';
import {ReportItem} from '@presentation/pages/machine-overview/report-item';
import {AssertionUtils, moment, Unit} from '@vdw/angular-component-library';
import {interval, map, Observable, of, startWith} from 'rxjs';

@Pipe({
  name: 'reportItemFormat'
})
export class ReportItemFormatPipe implements PipeTransform {
  public transform(reportItem: ReportItem): Observable<string> {
    if (AssertionUtils.isNullOrUndefined(reportItem?.value) || reportItem?.value === '') {
      return of('');
    } else if (reportItem.field === ReportItemTemplateField.RUN_TIMESTAMP || reportItem.field === ReportItemTemplateField.STOP_TIMESTAMP) {
      return this.startTimer(reportItem.value);
    } else if (reportItem.field === ReportItemTemplateField.REMAINING_TIME) {
      return of(this.convertToTimeSpan(reportItem.value.toString()));
    } else if (reportItem.field === ReportItemTemplateField.ESTIMATED_TIME_OF_COMPLETION) {
      return of(this.convertToDateTime(reportItem.value.toString()));
    } else if (!isNaN(+reportItem.value)) {
      return of(this.formatReportItem(+reportItem.value, reportItem.field));
    } else {
      return of(reportItem.value?.toString());
    }
  }

  private startTimer(value: string): Observable<string> {
    return interval(1000).pipe(
      startWith('0'),
      map(() => {
        const duration = moment().diff(value);
        return this.convertToTimeSpan(duration.toString());
      })
    );
  }

  private convertToTimeSpan(value: string): string {
    const momentDuration = moment.duration(value);

    if (momentDuration.asSeconds() === 0) {
      return '';
    } else if (momentDuration.asSeconds() < 60) {
      return '0:' + momentDuration.seconds().toString().padStart(2, '0');
    } else {
      return momentDuration.format('d.hh:mm:ss');
    }
  }

  private convertToDateTime(value: string): string {
    return moment(value).format('DD MMM. HH:mm').toString();
  }

  private formatReportItem(value: number, field: ReportItemTemplateField): string {
    switch (field) {
      case ReportItemTemplateField.MACHINE_EFFICIENCY_VALUE:
      case ReportItemTemplateField.GROUP_EFFICIENCY_VALUE:
      case ReportItemTemplateField.OPERATOR_EFFICIENCY:
      case ReportItemTemplateField.SPEED_EFFICIENCY:
      case ReportItemTemplateField.ORDER_PROGRESS:
      case ReportItemTemplateField.SPINDLE_EFFICIENCY:
      case ReportItemTemplateField.SYSTEM_EFFICIENCY:
      case ReportItemTemplateField.OVERALL_EFFICIENCY:
      case ReportItemTemplateField.PUMP_SPEED:
        if (value >= 100) {
          return `100 ${Unit.PERCENTAGE}`;
        }
        return `${this.convertToDecimals(value, 1)} ${Unit.PERCENTAGE}`;
      case ReportItemTemplateField.PRODUCTION:
        return `${this.convertToDecimals(value, 2)} ${Unit.METER}`;
      case ReportItemTemplateField.CUTS_PER_10KM:
      case ReportItemTemplateField.JOINTS_PER_10KM:
      case ReportItemTemplateField.YARN_FAULTS_PER_10KM:
        return `${this.convertToDecimals(value, 3)}`;
      case ReportItemTemplateField.STOP_GROUPS:
      case ReportItemTemplateField.QTY_PRODUCED:
      case ReportItemTemplateField.QTY_REQUIRED:
        return `${this.convertToDecimals(value, 2)}`;
      case ReportItemTemplateField.WATER_CONSUMPTION:
      case ReportItemTemplateField.STEAM_CONSUMPTION:
        return `${this.convertToDecimals(value, 2)} ${Unit.CUBIC_METERS}`;
      case ReportItemTemplateField.SEC_AIR:
        return `${this.convertToDecimals(value, 2)} ${Unit.NORMAL_CUBIC_METER_PER_SQUARE_METER}`;
      case ReportItemTemplateField.SEC_POWER:
        return `${this.convertToDecimals(value, 2)} ${Unit.KILOWATT_HOUR_PER_SQUARE_METER}`;
      case ReportItemTemplateField.LIQUOR_RATIO:
      case ReportItemTemplateField.STANDARD_SPEED:
        return `${this.convertToDecimals(value, 1)}`;
      case ReportItemTemplateField.WEIGHT:
        return `${this.convertToDecimals(value, 1)} ${Unit.KILOGRAM}`;
      case ReportItemTemplateField.POWER_CONSUMPTION:
        return `${this.convertToDecimals(value, 1)} ${Unit.KILOWATT_HOUR}`;
      case ReportItemTemplateField.AIR_CONSUMPTION:
        return `${this.convertToDecimals(value, 1)} ${Unit.NORMAL_CUBIC_METER}`;
      case ReportItemTemplateField.PRODUCTIVE_SPEED:
      case ReportItemTemplateField.REEL_SPEED:
        return `${this.convertToDecimals(value, 1)} ${Unit.METER_PER_MINUTE}`;
      case ReportItemTemplateField.TEMP_MAIN_TANK:
      case ReportItemTemplateField.TEMP_SIDE_TANK:
        return `${this.convertToDecimals(value, 1)} ${Unit.DEGREES_CELSIUS}`;
      case ReportItemTemplateField.AVERAGE_RPM:
      case ReportItemTemplateField.ACTUAL_ROTATIONS_PER_MINUTE:
      case ReportItemTemplateField.PACKAGES:
        return `${this.convertToDecimals(value, 0)}`;
      case ReportItemTemplateField.BATCH_LOAD:
        return `${this.convertToDecimals(value, 0)} ${Unit.KILOGRAM}`;

      default:
        return `${value}`;
    }
  }

  private convertToDecimals(value: number, amountOfDecimals: number): string {
    return value === 0 ? value.toString() : value.toFixed(amountOfDecimals);
  }
}
